import * as React from "react";

import { Card, CardBody, CardHeader, Skeleton } from "Components/ui";
import type { RoleMix } from "../data";

interface RoleMixCardProps {
  mix: RoleMix;
  loading: boolean;
}

/** Share of users whose role name contains "admin" or "support"; everyone else counts as users. */
const RoleMixCard: React.FC<RoleMixCardProps> = ({ mix, loading }) => {
  const segments = [
    {
      label: "Users",
      count: Math.max(0, mix.total - mix.admins - mix.support),
      pct: mix.userPct,
      swatch: "bg-chart-1",
    },
    { label: "Admins", count: mix.admins, pct: mix.adminPct, swatch: "bg-chart-4" },
    { label: "Support", count: mix.support, pct: mix.supportPct, swatch: "bg-chart-6" },
  ];

  return (
    <Card>
      <CardHeader
        title="Role mix"
        description={
          loading
            ? "Loading…"
            : `${mix.total} ${mix.total === 1 ? "user" : "users"}, ${mix.active} active`
        }
      />
      <CardBody className="space-y-4">
        {loading ? (
          <>
            <Skeleton className="h-2 w-full rounded-full" />
            <div className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </>
        ) : (
          <>
            <div
              aria-hidden="true"
              className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-surface-muted"
            >
              {segments.map((segment) =>
                segment.pct > 0 ? (
                  <span
                    key={segment.label}
                    className={segment.swatch}
                    style={{ width: `${segment.pct}%` }}
                  />
                ) : null
              )}
            </div>
            <ul className="space-y-2.5 text-sm">
              {segments.map((segment) => (
                <li key={segment.label} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <span aria-hidden="true" className={`h-2 w-2 rounded-full ${segment.swatch}`} />
                    {segment.label}
                  </span>
                  <span className="tabular-nums text-foreground">
                    {segment.pct}%
                    <span className="ml-2 text-muted-foreground">{segment.count}</span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default RoleMixCard;
